import React, { useState, useEffect, useCallback } from 'react'
import { T, Avatar, Spinner } from './ui'
import {
  getCommunityMe, setCommunityHandle, logNFLPick,
  reactNFL, getNFLComments, postNFLComment, reportNFL, getCommunityGuidelines,
  getMyModerationNotices,
} from './api'

// Shared community pieces used by both the Community tab and the player modal:
// the identity/handle bar, the "log a pick" sheet, reaction bar, comments sheet,
// and the graded-result badge. Identity is resolved server-side from the paid
// access code OR the free token (see api.js _withTok) — a user who is neither
// sees a gentle "start free to post" prompt instead of a silent 401.

// ── identity ─────────────────────────────────────────────────────────────────
export function useMe() {
  const [me, setMe] = useState(null)   // null = loading
  const refresh = useCallback(() => {
    getCommunityMe().then(d => setMe(d || { signed_in: false, handle: '' }))
  }, [])
  useEffect(() => { refresh() }, [refresh])
  return { me, refresh }
}

export function SignInPrompt({ compact }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
      padding: compact ? '12px 14px' : '20px 16px', textAlign: 'center' }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, marginBottom: 4 }}>Join the community</div>
      <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
        Start free (or sign in) to log picks, post to The Wall, vote on the roadmap, and climb the leaderboard.</div>
      <a href="?free=1" style={{ display: 'inline-block', marginTop: 11, fontSize: 12.5, fontWeight: 800,
        color: '#04140D', background: T.green, borderRadius: 9, padding: '8px 16px', textDecoration: 'none' }}>
        Start free →</a>
    </div>
  )
}

export function HandleBar({ me, onChange }) {
  const [edit, setEdit] = useState(false)
  const [val, setVal] = useState(me?.handle || '')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  if (!me?.signed_in) return null
  const save = async () => {
    setBusy(true); setErr('')
    try { const r = await setCommunityHandle(val.trim()); setEdit(false); onChange && onChange(r.handle) }
    catch (e) { setErr(e.message || 'Could not save') }
    finally { setBusy(false) }
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.muted, marginBottom: 12 }}>
      {!edit ? (
        <>
          <span>You post as <b style={{ color: T.green }}>@{me.handle}</b></span>
          <button onClick={() => { setVal(me.handle); setEdit(true) }} style={{ background: 'none', border: 'none',
            color: T.muted2, fontSize: 11.5, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>change</button>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <input value={val} onChange={e => setVal(e.target.value)} maxLength={24} autoFocus
            style={{ background: T.bg, border: `1px solid ${T.border2}`, borderRadius: 8, padding: '6px 9px',
              color: T.text, fontSize: 12.5, width: 150 }} />
          <button onClick={save} disabled={busy} style={{ background: T.green, color: '#04140D', border: 'none',
            borderRadius: 8, padding: '6px 11px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer' }}>Save</button>
          <button onClick={() => setEdit(false)} style={{ background: 'none', border: 'none', color: T.muted,
            fontSize: 11.5, cursor: 'pointer' }}>cancel</button>
          {err && <span style={{ color: T.red, fontSize: 11 }}>{err}</span>}
        </div>
      )}
    </div>
  )
}

// ── removal notices (the bot tells you, transparently, when a post came down) ──
export function RemovalNotices({ signedIn, onSeeGuidelines }) {
  const [n, setN] = useState([])
  useEffect(() => { if (signedIn) getMyModerationNotices().then(r => setN(r.notices || [])) }, [signedIn])
  if (!n.length) return null
  return (
    <div style={{ background: T.amberDim, border: `1px solid ${T.amber}66`, borderRadius: 12,
      padding: '11px 13px', marginBottom: 12 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: T.amber, marginBottom: 4 }}>
        {n.length === 1 ? 'A post was removed' : `${n.length} posts were removed`}</div>
      {n.slice(0, 3).map((x, i) => (
        <div key={i} style={{ fontSize: 11.5, color: T.muted2, lineHeight: 1.45, marginTop: 2 }}>
          · Your {x.kind} — <b style={{ color: T.text }}>{x.reason || x.category || 'guidelines'}</b>
          {x.snippet ? <span style={{ color: T.faint }}> ("{x.snippet.slice(0, 50)}…")</span> : null}
        </div>
      ))}
      <button onClick={onSeeGuidelines} style={{ marginTop: 7, background: 'none', border: 'none',
        color: T.amber, fontSize: 11.5, fontWeight: 800, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
        Review the guidelines →</button>
    </div>
  )
}

// ── community guidelines ──────────────────────────────────────────────────────
export function GuidelinesSheet({ onClose }) {
  const [g, setG] = useState(null)
  useEffect(() => { getCommunityGuidelines().then(r => setG(r.guidelines || [])) }, [])
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 400,
      display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, margin: '0 auto',
        background: T.card2, borderRadius: '16px 16px 0 0', border: `1px solid ${T.border2}`, maxHeight: '82vh',
        overflowY: 'auto', padding: '16px 16px 24px' }}>
        <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 3 }}>Community guidelines</div>
        <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 14, lineHeight: 1.5 }}>
          An automated moderation bot keeps the community on the rails — posts that break these get removed, and
          repeat or clear violations are hidden for review. Keep it sharp and keep it clean.</div>
        {g === null ? <Spinner /> : g.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: `1px solid ${T.border}` }}>
            <span style={{ color: T.green, fontWeight: 900, fontSize: 13 }}>{i + 1}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 800 }}>{r.title}</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2, lineHeight: 1.45 }}>{r.detail}</div>
            </div>
          </div>
        ))}
        <div style={{ fontSize: 10.5, color: T.faint, marginTop: 14, lineHeight: 1.5 }}>
          21+ · LINEBRKR is a research tool, not betting advice. Problem gambling? Call 1-800-GAMBLER.</div>
      </div>
    </div>
  )
}

// ── graded-result badge ───────────────────────────────────────────────────────
const RESULT_STYLE = {
  win:     { bg: T.greenDim, fg: T.green, label: 'WON' },
  loss:    { bg: T.redDim, fg: T.red, label: 'LOST' },
  push:    { bg: T.card2, fg: T.muted2, label: 'PUSH' },
  dnp:     { bg: T.card2, fg: T.faint, label: 'DNP' },
  pending: { bg: T.amberDim, fg: T.amber, label: 'LIVE' },
}
export function ResultBadge({ result }) {
  const s = RESULT_STYLE[result] || RESULT_STYLE.pending
  return <span style={{ fontSize: 10, fontWeight: 900, color: s.fg, background: s.bg,
    borderRadius: 6, padding: '2px 7px', letterSpacing: 0.3 }}>{s.label}</span>
}

// ── log-a-pick sheet (opened from the player modal) ────────────────────────────
// `pick` carries what the board already knows: { season, week, gsis_id, prop_type,
// market, line, disp_prob, name, team, pos, isTD }.
export function LogPickSheet({ pick, onClose, onLogged }) {
  const [side, setSide] = useState('over')
  const [pub, setPub] = useState(true)
  const [take, setTake] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const lineLabel = pick.isTD ? 'Anytime TD' : `${pick.market} ${side === 'over' ? 'O' : 'U'}${pick.line}`
  const submit = async () => {
    setBusy(true); setErr('')
    try {
      await logNFLPick({
        season: pick.season, week: pick.week, gsis_id: pick.gsis_id, prop_type: pick.prop_type,
        line: pick.line, side: pick.isTD ? 'over' : side, disp_prob: pick.disp_prob,
        name: pick.name, team: pick.team, pos: pick.pos, take: take.trim(), is_public: pub,
      })
      onLogged && onLogged()
      onClose()
    } catch (e) {
      if (/sign in|start free/i.test(e.message || '')) setErr('Start free (or sign in) to log picks.')
      else setErr(e.message || 'Could not log that pick.')
      setBusy(false)
    }
  }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 400,
      display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, margin: '0 auto',
        background: T.card2, borderRadius: '16px 16px 0 0', border: `1px solid ${T.border2}`, padding: '16px 16px 24px' }}>
        <div style={{ fontSize: 15, fontWeight: 900, marginBottom: 2 }}>Log this play</div>
        <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 14 }}>
          <b style={{ color: T.text }}>{pick.name}</b> · {lineLabel}
          {pick.disp_prob ? <span style={{ color: T.green, fontWeight: 800 }}> · {Math.round(pick.disp_prob * 100)}%</span> : null}
        </div>

        {!pick.isTD && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {['over', 'under'].map(s => (
              <button key={s} onClick={() => setSide(s)} style={{ flex: 1, padding: '9px 0', borderRadius: 9,
                border: `1px solid ${side === s ? T.green : T.border2}`, cursor: 'pointer', fontWeight: 800, fontSize: 12.5,
                background: side === s ? T.greenDim : 'transparent', color: side === s ? T.green : T.muted }}>
                {s === 'over' ? `Over ${pick.line}` : `Under ${pick.line}`}</button>
            ))}
          </div>
        )}

        <textarea value={take} onChange={e => setTake(e.target.value)} maxLength={280}
          placeholder="Add a take (optional) — why you like it…" rows={2}
          style={{ width: '100%', boxSizing: 'border-box', background: T.bg, border: `1px solid ${T.border2}`,
            borderRadius: 10, padding: '10px 12px', color: T.text, fontSize: 12.5, resize: 'none', marginBottom: 12 }} />

        <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: T.muted2,
          marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={pub} onChange={e => setPub(e.target.checked)}
            style={{ width: 17, height: 17, accentColor: T.green }} />
          Post to <b style={{ color: T.text }}>The Wall</b> — it shows your W/L once it settles.
        </label>

        {err && <div style={{ color: T.red, fontSize: 12, marginBottom: 10 }}>{err}</div>}
        <button onClick={submit} disabled={busy} style={{ width: '100%', padding: '12px 0', borderRadius: 11,
          border: 'none', background: T.green, color: '#04140D', fontWeight: 900, fontSize: 13.5,
          cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Logging…' : pub ? 'Log + post to Wall' : 'Log pick'}</button>
      </div>
    </div>
  )
}

// A compact button for the player modal that opens the log sheet.
export function LogPickButton({ pick }) {
  const [open, setOpen] = useState(false)
  const [done, setDone] = useState(false)
  return (
    <>
      <button onClick={() => setOpen(true)} style={{ width: '100%', padding: '10px 0', marginBottom: 12,
        borderRadius: 10, cursor: 'pointer', fontWeight: 800, fontSize: 12.5,
        border: `1px solid ${done ? T.green : T.border2}`, background: done ? T.greenDim : 'transparent',
        color: done ? T.green : T.muted2 }}>
        {done ? '✓ Logged' : '＋ Log this pick'}</button>
      {open && <LogPickSheet pick={pick} onClose={() => setOpen(false)} onLogged={() => setDone(true)} />}
    </>
  )
}

// ── reactions ───────────────────────────────────────────────────────────────
const REACTS = [{ k: 'fire', e: '🔥' }, { k: 'tail', e: '🎯' }, { k: 'fade', e: '🙅' }]
export function ReactionBar({ targetType = 'pick', targetId, counts = {}, signedIn }) {
  const [c, setC] = useState(counts)
  const [mine, setMine] = useState({})
  const toggle = async (kind) => {
    if (!signedIn) { window.location.search = '?free=1'; return }
    try {
      const r = await reactNFL(targetType, String(targetId), kind)
      setC(r.counts || {})
      setMine(m => ({ ...m, [kind]: r.mine?.[kind] }))
    } catch (_) {}
  }
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {REACTS.map(({ k, e }) => (
        <button key={k} onClick={() => toggle(k)} style={{ display: 'flex', alignItems: 'center', gap: 4,
          background: mine[k] ? T.greenDim : T.card2, border: `1px solid ${mine[k] ? T.green : T.border}`,
          borderRadius: 999, padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: T.muted2 }}>
          <span>{e}</span>{c[k] ? <b style={{ color: T.text, fontSize: 11.5 }}>{c[k]}</b> : null}
        </button>
      ))}
    </div>
  )
}

// ── comments ──────────────────────────────────────────────────────────────────
export function CommentsSheet({ targetType = 'pick', targetId, signedIn, onClose }) {
  const [d, setD] = useState(null)
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const load = useCallback(() => {
    getNFLComments(targetType, String(targetId)).then(r => setD(r.comments || []))
  }, [targetType, targetId])
  useEffect(() => { load() }, [load])
  const add = async () => {
    if (!body.trim()) return
    setBusy(true); setErr('')
    try { await postNFLComment(targetType, String(targetId), body.trim()); setBody(''); load() }
    catch (e) { setErr(e.message || 'Could not post.') } finally { setBusy(false) }
  }
  const report = async (id) => { try { await reportNFL('comment', String(id), 'reported from app'); load() } catch (_) {} }
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 400,
      display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 460, margin: '0 auto',
        background: T.card2, borderRadius: '16px 16px 0 0', border: `1px solid ${T.border2}`, maxHeight: '82vh',
        overflowY: 'auto', padding: '14px 16px 20px' }}>
        <div style={{ fontSize: 14, fontWeight: 900, marginBottom: 12 }}>Comments</div>
        {d === null ? <Spinner /> : d.length === 0
          ? <div style={{ fontSize: 12, color: T.muted, padding: '8px 0 16px' }}>No comments yet — start the thread.</div>
          : d.map(c => (
            <div key={c.id} style={{ borderBottom: `1px solid ${T.border}`, padding: '9px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: T.green }}>@{c.handle}</span>
                <button onClick={() => report(c.id)} title="Report" style={{ background: 'none', border: 'none',
                  color: T.faint, fontSize: 11, cursor: 'pointer' }}>report</button>
              </div>
              <div style={{ fontSize: 13, color: T.text, marginTop: 3, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{c.body}</div>
            </div>
          ))}
        {signedIn ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <input value={body} onChange={e => setBody(e.target.value)} maxLength={1000} placeholder="Add a comment…"
              onKeyDown={e => { if (e.key === 'Enter') add() }}
              style={{ flex: 1, background: T.bg, border: `1px solid ${T.border2}`, borderRadius: 9, padding: '9px 11px',
                color: T.text, fontSize: 12.5 }} />
            <button onClick={add} disabled={busy} style={{ background: T.green, color: '#04140D', border: 'none',
              borderRadius: 9, padding: '0 15px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>Post</button>
          </div>
        ) : null}
        {err && <div style={{ color: T.red, fontSize: 11.5, marginTop: 8 }}>{err}</div>}
        {!signedIn && (
          <div style={{ marginTop: 12 }}><SignInPrompt compact /></div>
        )}
      </div>
    </div>
  )
}
